import { Controller } from 'stimulus'
import $ from 'jquery'
import { getStripe } from '~/common/components/stripe'
import { hiddenWallets } from '~/common/components/stripe/wallets'
import {
  expressCheckoutBlocker,
  fieldLabel,
  isDeferredExpressCheckoutField,
  updateCartOnEmailChange,
} from '~/site/pages/cart/common'
import { suspendStripeSetup } from '~/site/pages/cart/payment/stripe'
import { prepareStripeCheckout } from '~/site/pages/cart/payment/stripe_setup'
import { processFormSubmission } from '~/site/pages/cart/payment/common'
import { PAYMENT_BLOCKING_ATTRIBUTES } from '~/site/pages/cart/vat_check_lock'

// A cart refresh can replace the controller while its wallet is confirming.
const activeConfirmations = new Map()

export default class extends Controller {
  static targets = [
    'content',
    'skeleton',
    'wallets',
    'termsNotice',
    'terms',
    'termsLink',
    'termsTemplate',
    'noticeTemplate',
    'errorTemplate',
  ]
  static values = {
    processorId: Number,
    publishableKey: String,
    amount: Number,
    currency: String,
    recurring: Boolean,
    refreshCart: { type: Boolean, default: true },
    orderTimeout: { type: Number, default: 15000 },
    captureMethod: String,
    excludedTypes: Array,
    excludedWallets: Array,
    failedMessage: String,
    fieldRequiredMessage: String,
    detailsRequiredMessage: String,
    quoteChangedMessage: String,
  }

  connect() {
    if (this.confirmationStarted) return

    this.form = this.element.closest('form')
    this.checkoutPath = window.location.pathname
    this.start()
  }

  start() {
    this.expressCheckout = false
    this.mountVersion = (this.mountVersion || 0) + 1
    this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)')
    this.element.hidden = false
    this.setAvailable(true)
    this.setLoading(true)
    this.mount(this.mountVersion)
  }

  disconnect() {
    this.stop()
  }

  async mount(version) {
    try {
      const stripe = await getStripe(this.publishableKeyValue)
      if (version !== this.mountVersion || !this.element.isConnected) return

      while (this.confirmationPending) {
        await activeConfirmations.get(this.checkoutPath).confirmationReleased
        if (version !== this.mountVersion || !this.element.isConnected) return
      }

      const mode = this.amountValue === 0 ? 'setup' : 'payment'
      this.elements = stripe.elements({
        mode,
        ...(mode === 'payment'
          ? { amount: this.amountValue, captureMethod: this.captureMethodValue }
          : {}),
        currency: this.currencyValue,
        ...(this.recurringValue ? { setupFutureUsage: 'off_session' } : {}),
        ...(this.excludedTypesValue.length
          ? { excludedPaymentMethodTypes: this.excludedTypesValue }
          : {}),
        appearance: { theme: this.theme },
      })
      const paymentMethods = {
        ...(import.meta.env.DEV
          ? { applePay: 'always', googlePay: 'always' }
          : {}),
        ...hiddenWallets(this.excludedWalletsValue),
      }
      this.wallets = this.elements.create('expressCheckout', {
        shippingAddressRequired: false,
        billingAddressRequired: true,
        emailRequired: true,
        buttonHeight: 48,
        buttonType: { applePay: 'plain', googlePay: 'plain' },
        layout: this.walletLayout,
        ...(Object.keys(paymentMethods).length ? { paymentMethods } : {}),
      })

      this.wallets.on('availablepaymentmethodschange', ({ paymentMethods }) => {
        if (version !== this.mountVersion) return

        this.setLoading(false)
        this.setAvailable(
          Object.values(paymentMethods || {}).some((method) => method.available)
        )
      })
      this.wallets.on('loaderror', this.stop)

      this.wallets.on('click', (event) => {
        // The wallet button lives in Stripe's iframe, so the seat hold can't see this interaction.
        window.dispatchEvent(new CustomEvent('capacity-hold:start'))
        if (
          this.expressCheckout ||
          this.confirmationPending ||
          this.cartUpdatePending
        )
          event.reject()
        else event.resolve({})
      })
      this.wallets.on('confirm', this.confirm)
      this.wallets.mount(this.walletsTarget)
      this.updateTermsNotice()
      this.desktopMediaQuery.addEventListener('change', this.updateLayout)

      this.themeObserver = new MutationObserver(() => {
        this.elements.update({ appearance: { theme: this.theme } })
      })
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      })
    } catch (error) {
      if (version !== this.mountVersion) return

      this.stop()
      console.warn('Stripe Express Checkout could not load', error)
    }
  }

  // Includes the VAT-lookup lock: a wallet click never goes through
  // handlePaymentFormSubmit, so without it Apple/Google Pay would confirm against a total
  // whose VIES answer is still in flight. Shared list so the two payment paths cannot drift.
  get cartUpdatePending() {
    return PAYMENT_BLOCKING_ATTRIBUTES.some((attribute) =>
      this.form?.hasAttribute(attribute)
    )
  }

  get confirmationPending() {
    return activeConfirmations.has(this.checkoutPath)
  }

  confirm = async (event) => {
    if (import.meta.env.DEV) {
      console.log(
        '[Stripe Express Checkout] prefill payload',
        JSON.stringify(
          {
            expressPaymentType: event.expressPaymentType,
            billingAddressRequired: true,
            billingDetails: event.billingDetails,
          },
          null,
          2
        )
      )
    }

    if (this.expressCheckout) return
    if (
      !this.form ||
      !this.element.isConnected ||
      this.confirmationPending ||
      this.cartUpdatePending
    ) {
      event.paymentFailed({ reason: 'fail' })
      return
    }

    try {
      this.startExpressCheckout(event)
      await this.prefill(event.billingDetails || {})
      if (this.confirmEvent !== event) return

      this.checkRequiredCheckboxes()
      const { error } = await this.elements.submit()
      if (error) throw error
      if (this.confirmEvent !== event) return

      await this.prepareIntent(event)
      if (this.confirmEvent !== event) return

      await this.prepareOrder(event)
      if (this.confirmEvent !== event) return
      if (!this.order) {
        this.errorMessage ||= this.blockedMessage()
        return
      }

      this.confirmationReturnUrl = this.order.callback_url
      this.confirmationStarted = true
      const result = await this.confirmPayment()
      if (this.confirmationFailed(result)) {
        this.confirmationStarted = false
        this.errorMessage = result.error?.message || this.failedMessageValue
      } else {
        this.completeConfirmation()
      }
    } catch (error) {
      console.warn('Stripe Express Checkout could not complete checkout', error)
      if (this.confirmationStarted) {
        if (this.confirmationFailed({ error })) {
          this.confirmationStarted = false
          this.errorMessage = error.message || this.failedMessageValue
        } else {
          // A network error after confirmation does not tell us whether money
          // moved. Keep checkout blocked and let the existing return page check.
          this.completeConfirmation()
        }
      } else if (
        error?.type === 'validation_error' ||
        error?.type === 'card_error'
      ) {
        this.errorMessage = error.message
      }
    } finally {
      if (this.confirmEvent === event && !this.confirmationStarted) {
        await this.recover(this.errorMessage || this.failedMessageValue)
      }
    }
  }

  async prepareIntent(event) {
    const abortController = new AbortController()
    this.setupAbortController = abortController
    const timeout = setTimeout(() => abortController.abort(), 25000)

    try {
      const intent = await prepareStripeCheckout(
        this.form,
        this.processorIdValue,
        'card',
        { signal: abortController.signal }
      )
      if (this.confirmEvent !== event) return

      const intentType = this.amountValue === 0 ? 'setup' : 'payment'
      const intentPrefix = intentType === 'setup' ? 'seti_' : 'pi_'
      if (
        intent.publishable_key !== this.publishableKeyValue ||
        intent.intent_type !== intentType ||
        typeof intent.intent_id !== 'string' ||
        !intent.intent_id.startsWith(intentPrefix)
      ) {
        throw new Error('Stripe returned an incompatible checkout intent')
      }
      if (!this.matchesQuote(intent.express_checkout_quote)) {
        this.errorMessage = this.quoteChangedMessageValue
        throw new Error(this.errorMessage)
      }
      this.intent = intent
    } finally {
      clearTimeout(timeout)
      if (this.setupAbortController === abortController)
        this.setupAbortController = null
    }
  }

  matchesQuote(quote) {
    if (!quote) return false

    const setup = this.amountValue === 0
    return (
      quote.amount === this.amountValue &&
      quote.currency === this.currencyValue &&
      quote.mode === (setup ? 'setup' : 'payment') &&
      quote.setup_future_usage ===
        (setup || this.recurringValue ? 'off_session' : null) &&
      quote.capture_method === (setup ? null : this.captureMethodValue)
    )
  }

  async prepareOrder(event) {
    const paymentForm = this.form.querySelector(
      '[data-controller~="payment-processor--payment-form"]'
    )
    const paymentController =
      paymentForm &&
      this.application.getControllerForElementAndIdentifier(
        paymentForm,
        'payment-processor--payment-form'
      )
    paymentController?.ensureFirstEntrantIsPopulated({ target: this.form })

    const abortController = new AbortController()
    this.orderAbortController = abortController
    const timeout = setTimeout(
      () => abortController.abort(),
      this.orderTimeoutValue
    )

    try {
      const order = await processFormSubmission(this.form, {
        expressCheckout: true,
        signal: abortController.signal,
        getFormData: () => {
          const formData = new FormData(this.form)
          formData.set('intent_id', this.intent.intent_id)
          formData.set('intent_type', this.intent.intent_type)
          return formData
        },
        onSuccess: (json) => json,
        onError: (error) => {
          console.warn('Stripe Express Checkout could not prepare order', error)
          this.errorMessage =
            error?.replace?.['cart-errors-container'] &&
            typeof error.error === 'string'
              ? error.error
              : this.failedMessageValue
        },
      })
      if (this.confirmEvent !== event || !order) return

      if (
        order.success !== true ||
        typeof order.callback_url !== 'string' ||
        !order.callback_url.trim() ||
        !['http:', 'https:'].includes(new URL(order.callback_url).protocol)
      ) {
        throw new Error('Checkout returned an invalid payment callback')
      }
      this.order = order
    } finally {
      clearTimeout(timeout)
      if (this.orderAbortController === abortController)
        this.orderAbortController = null
    }
  }

  async confirmPayment() {
    // Keep the exact wallet Elements and callback even if Stimulus disconnects
    // while Stripe is awaiting authentication or a network response.
    const { elements, intent, order } = this
    const stripe = await getStripe(this.publishableKeyValue)
    const options = {
      elements,
      clientSecret: intent.client_secret,
      confirmParams: { return_url: order.callback_url },
      redirect: 'if_required',
    }

    return intent.intent_type === 'setup'
      ? stripe.confirmSetup(options)
      : stripe.confirmPayment(options)
  }

  confirmationFailed(result) {
    const intent =
      result?.error?.payment_intent ||
      result?.error?.setup_intent ||
      result?.paymentIntent ||
      result?.setupIntent
    if (!intent) return result?.error?.type === 'validation_error'

    return (
      intent.id === this.intent.intent_id &&
      ['requires_payment_method', 'canceled'].includes(intent.status)
    )
  }

  completeConfirmation() {
    window.location.assign(this.confirmationReturnUrl)
  }

  // Nothing reached the server, so the form itself says what is in the way. Read before the wallet
  // is released: a cart refresh on the way out can replace the field we want to point at.
  blockedMessage() {
    this.blocker = expressCheckoutBlocker(this.form)
    const label = this.blocker?.field && fieldLabel(this.blocker.field)

    return label
      ? this.fieldRequiredMessageValue.replace('%{field}', label)
      : this.detailsRequiredMessageValue
  }

  async recover(message) {
    const form = this.form
    const error =
      this.errorTemplateTarget.content.firstElementChild.cloneNode(true)
    error.querySelector('span').textContent = message
    const resumeStripeSetup = this.failConfirmation({ restartStripe: false })
    if (!this.element.isConnected) return

    this.stop()
    try {
      // Refresh prices after releasing the wallet. Failed refreshes keep the
      // stale wallet stopped; the original payment options can still recover.
      // A refresh also renders the fresh wallet.
      if (this.refreshCartValue) await updateCartOnEmailChange()
      else this.start()
    } finally {
      resumeStripeSetup?.()
      if (form.isConnected) {
        const container = document.getElementById('cart-errors-container')
        container?.replaceChildren(error)
        // The alert announces itself either way; send the cursor to the field that needs filling
        // in, and let the browser mark it, when we still have one.
        if (this.blocker?.field?.isConnected)
          this.blocker.field.reportValidity()
        else error.querySelector('[role="alert"]').focus()
      }
    }
  }

  startExpressCheckout(event) {
    this.expressCheckout = true
    this.confirmationReleased = new Promise((resolve) => {
      this.releaseConfirmation = resolve
    })
    activeConfirmations.set(this.checkoutPath, this)
    this.errorMessage = null
    this.blocker = null
    this.confirmEvent = event
    document.addEventListener('submit', this.preventOrdinaryPayment, true)
    document.addEventListener('click', this.preventOrdinaryPayment, true)

    this.expressCheckoutField = document.createElement('input')
    this.expressCheckoutField.type = 'hidden'
    this.expressCheckoutField.name = 'express_checkout'
    this.expressCheckoutField.value = '1'
    this.form.appendChild(this.expressCheckoutField)

    this.paymentMethods = this.form.querySelector('.cart-payment-methods')
    if (!this.paymentMethods) throw new Error('Payment options are unavailable')

    this.resumeStripeSetup = suspendStripeSetup()
    this.paymentNotice =
      this.noticeTemplateTarget.content.firstElementChild.cloneNode(true)
    const cancelButton = this.paymentNotice.querySelector('button')
    cancelButton.disabled = true
    cancelButton.addEventListener('click', this.cancel)
    this.paymentMethods.replaceWith(this.paymentNotice)

    this.processorField = document.createElement('input')
    this.processorField.type = 'hidden'
    this.processorField.name = 'purchase[payment_method_id]'
    this.processorField.value = `pp-${this.processorIdValue}-card`
    this.form.appendChild(this.processorField)
  }

  preventOrdinaryPayment = (event) => {
    if (!this.expressCheckout) return
    const form =
      event.type === 'submit'
        ? event.target
        : event.target.closest?.('.submit-payment')?.form
    const replacedCheckout =
      this.confirmationStarted &&
      window.location.pathname === this.checkoutPath &&
      form?.matches('form.cart-checkout-payment')
    if (form !== this.form && !replacedCheckout) return

    // Includes the mobile button outside the form. The ordinary manual-payment
    // handler otherwise mistakes the detached options for a free checkout.
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  cancel = () => {
    // Once the wallet confirms, the active payment flow owns its outcome.
    if (this.confirmEvent) return

    this.failConfirmation()
  }

  async prefill(details) {
    const name = details.name?.trim()
    if (name) {
      this.fillField('name', name)
      const [firstName, ...lastName] = name.split(/\s+/)
      this.fillField('first_names', firstName)
      this.fillField('last_name', lastName.join(' '))
    }
    this.fillField('email', details.email)

    const address = details.address || {}
    const country = this.field('country_code')
    const previousCountry = country?.value
    this.fillField('country_code', address.country)
    const addressElement = country?.closest(
      '[data-controller~="address-field"]'
    )
    const addressController =
      addressElement &&
      this.application.getControllerForElementAndIdentifier(
        addressElement,
        'address-field'
      )

    if (country && country.value !== previousCountry && addressController) {
      addressController.setCountryForAutocomplete()
      const abortController = new AbortController()
      this.addressAbortController = abortController
      const timeout = setTimeout(() => abortController.abort(), 10000)
      try {
        await addressController.updateCountryFields(country.value, {
          signal: abortController.signal,
        })
      } finally {
        clearTimeout(timeout)
        this.addressAbortController = null
      }
      if (!this.element.isConnected || !this.expressCheckout) return
    }

    this.fillField('address', address.line1)
    this.fillField('address_2', address.line2)
    this.fillField('city', address.city)
    this.fillField('postal_code', address.postal_code)
    this.fillField('region_code', address.state)
    if (this.field('address_2')?.value)
      addressElement?.classList.add('address2-visible')

    // Reuse participant inheritance, including its protection for manually
    // entered recipients. Avoid the contact fields' ordinary change handlers.
    for (const field of ['name', 'first_names', 'last_name', 'email']) {
      $(this.field(field)).trigger('simplero:inherit')
    }
  }

  // Terms and consent boxes the buyer accepts by paying with their wallet. A required custom field
  // is a question we'd be answering for them, so it waits for the completion step instead.
  requiredCheckboxes() {
    return Array.from(this.form.elements).filter(
      (field) =>
        field.matches('input[type="checkbox"][required]:enabled') &&
        !isDeferredExpressCheckoutField(field.name)
    )
  }

  checkRequiredCheckboxes() {
    for (const field of this.requiredCheckboxes()) {
      if (field.checked) continue

      field.checked = true
      field.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // Say so when there is something to agree to, and let the buyer read it — links and all —
  // without leaving the wallet behind. The labels are the ones the form itself renders, so a
  // custom agreement reads the same here as it does further down the page.
  updateTermsNotice() {
    const checkboxes = this.requiredCheckboxes()
    this.hasTerms = checkboxes.length > 0
    this.showTermsNotice()
    if (!this.hasTerms) return

    const url = this.singleTermsUrl(checkboxes)
    this.termsLinkTarget.hidden = !url
    this.termsTarget.hidden = !!url
    if (url) {
      this.termsLinkTarget.href = url
      return
    }

    const content =
      this.termsTemplateTarget.content.firstElementChild.cloneNode(true)
    const list = content.querySelector('ul')
    const entry = list.querySelector('li')
    entry.remove()

    for (const checkbox of checkboxes) {
      const label = checkbox.labels?.[0] || checkbox.closest('label')
      if (!label) continue

      // The label's own markup, minus the box itself — a second checkbox inside a popover would
      // read as something else to tick.
      const text = label.cloneNode(true)
      text.querySelectorAll('input').forEach((input) => input.remove())
      const item = entry.cloneNode(true)
      item.append(text)
      list.append(item)
    }

    this.termsTarget.popoverContentEl = content
    // The popover may already have built itself around an empty content.
    this.termsTarget._tippy?.setContent(content)
  }

  // One box, and all it says is "I accept <the terms>". A popover there would be a click standing
  // in front of the very link it holds, so the notice points at that page itself. A box that opens
  // a contract modal has no page to point at, and one with its own wording still has something to
  // read, so both keep the popover.
  singleTermsUrl(checkboxes) {
    if (checkboxes.length !== 1) return null

    const label = checkboxes[0].labels?.[0] || checkboxes[0].closest('label')
    const links = Array.from(label?.querySelectorAll('a') || [])

    return links.length === 1 && /^https?:$/.test(links[0].protocol)
      ? links[0].href
      : null
  }

  field(name) {
    return (
      this.form.elements.namedItem(`purchase[${name}]`) ||
      this.form.elements.namedItem(`user[${name}]`)
    )
  }

  fillField(name, value) {
    const field = this.field(name)
    if (!field || field.disabled || field.readOnly || typeof value !== 'string')
      return

    if (field instanceof HTMLSelectElement) {
      const normalized = value.trim().toLowerCase()
      const option = Array.from(field.options).find(
        (option) =>
          option.value.toLowerCase() === normalized ||
          option.text.trim().toLowerCase() === normalized
      )
      field.value = option?.value || ''
    } else {
      field.value = value.trim()
    }
  }

  failConfirmation({ restartStripe = true } = {}) {
    if (this.confirmationStarted) return

    const event = this.confirmEvent
    this.confirmEvent = null
    this.intent = null
    this.order = null
    this.confirmationReturnUrl = null
    this.expressCheckout = false
    this.expressCheckoutField?.remove()
    this.expressCheckoutField = null
    this.processorField?.remove()
    this.processorField = null
    if (this.paymentMethods && this.paymentNotice?.parentNode) {
      this.paymentNotice.replaceWith(this.paymentMethods)
    }
    this.paymentMethods = null
    this.paymentNotice = null
    document.removeEventListener('submit', this.preventOrdinaryPayment, true)
    document.removeEventListener('click', this.preventOrdinaryPayment, true)

    if (activeConfirmations.get(this.checkoutPath) === this) {
      activeConfirmations.delete(this.checkoutPath)
      this.releaseConfirmation()
      this.releaseConfirmation = null
      this.confirmationReleased = null
    }

    const resumeStripeSetup = this.resumeStripeSetup
    this.resumeStripeSetup = null
    event?.paymentFailed({ reason: 'fail' })
    if (restartStripe) resumeStripeSetup?.()
    return resumeStripeSetup
  }

  get theme() {
    return document.documentElement.classList.contains('dark')
      ? 'night'
      : 'stripe'
  }

  get walletLayout() {
    return {
      maxColumns: this.desktopMediaQuery.matches ? 0 : 1,
      maxRows: 0,
      overflow: 'never',
    }
  }

  updateLayout = () => {
    if (!this.expressCheckout)
      this.wallets?.update({ layout: this.walletLayout })
  }

  setLoading(loading) {
    this.loading = loading
    this.skeletonTarget.hidden = !loading
    this.walletsTarget.classList.toggle('invisible', loading)
    this.contentTarget.setAttribute('aria-busy', String(loading))
    this.showTermsNotice()
  }

  // The notice belongs to the buttons — it waits for them rather than sitting over the skeleton.
  // Wallets the buyer has none of hide the block itself, notice included.
  showTermsNotice() {
    this.termsNoticeTarget.hidden = !this.hasTerms || this.loading
  }

  setAvailable(available) {
    for (const className of ['invisible', 'h-0', 'overflow-hidden']) {
      this.contentTarget.classList.toggle(className, !available)
    }
  }

  stop = () => {
    this.desktopMediaQuery?.removeEventListener('change', this.updateLayout)
    if (this.confirmationStarted) {
      this.themeObserver?.disconnect()
      return
    }

    this.addressAbortController?.abort()
    this.setupAbortController?.abort()
    this.orderAbortController?.abort()
    this.failConfirmation()
    this.mountVersion += 1
    this.element.hidden = true
    this.themeObserver?.disconnect()
    this.wallets?.destroy()
    this.wallets = null
    this.elements = null
  }
}
